import { h, FunctionComponent } from 'preact'
import classNames from 'classnames'
import { Button } from '@onfido/castor-react'

import { useSdkOptions } from '~contexts'
import { useLocales } from '~core/localisation'
import theme from 'components/Theme/style.scss'
import { trackComponent } from '../../Tracker'
import PageTitle from '../PageTitle'
import ScreenLayout from '../Theme/ScreenLayout'
import { DefaultContent, DocVideoContent } from './Content'
import style from './style.scss'
import type { StepComponentBaseProps } from '~types/routers'
import type { StepTypes } from '~types/steps'

const CAPTURE_STEP_TYPES: Set<StepTypes> = new Set([
  'document',
  'poa',
  'face',
  'data',
  'activeVideo',
])

type WelcomeActionsProps = {
  customNextButtonLabel?: string
  nextStep: () => void
  useWorkflow?: boolean
  isTrial?: boolean
}

const WelcomeActions: FunctionComponent<WelcomeActionsProps> = ({
  customNextButtonLabel,
  nextStep,
  useWorkflow,
  isTrial,
}) => {
  const { translate } = useLocales()
  const buttonLabel = customNextButtonLabel
    ? customNextButtonLabel
    : translate(
        `${
          useWorkflow
            ? isTrial
              ? 'welcome.start_workflow_button_trial'
              : 'welcome.start_workflow_button'
            : 'welcome.next_button'
        }`
      )

  return (
    <div className={theme.contentMargin}>
      <Button
        type="button"
        variant="primary"
        className={classNames(
          { [theme['button-trial']]: isTrial && useWorkflow },
          theme['button-centered'],
          theme['button-lg']
        )}
        onClick={nextStep}
        data-onfido-qa="welcome-next-btn"
      >
        {buttonLabel}
      </Button>
    </div>
  )
}

const TrialMessage = () => {
  const { translate } = useLocales()
  return (
    <div className={style.trialMessage}>
      <p>{translate('welcome.trial_message')}</p>
    </div>
  )
}

const Welcome: FunctionComponent<StepComponentBaseProps> = ({
  steps,
  nextStep,
  autoFocusOnInitialScreenTitle,
  isTrial,
}) => {
  const [{ useWorkflow }, { findStep }] = useSdkOptions()
  const { translate } = useLocales()

  const welcomeStep = findStep('welcome')
  const {
    title: customTitle,
    descriptions: customDescriptions,
    nextButton: customNextButtonLabel,
  } = welcomeStep?.options || {}
  const isFirstScreen = steps[0].type === 'welcome'

  const documentStep = findStep('document')
  const forDocVideo = documentStep?.options?.requestedVariant === 'video'

  const actions = (
    <WelcomeActions
      {...{ customNextButtonLabel, nextStep, useWorkflow, isTrial }}
    />
  )
  const welcomeTitle =
    customTitle ||
    translate('welcome.title', {
      trial: isTrial && useWorkflow ? ' (TEST)' : '',
    })
  const welcomeSubTitle = !customDescriptions
    ? translate('welcome.subtitle')
    : ''
  const captureSteps = steps
    .filter((step) => CAPTURE_STEP_TYPES.has(step.type))
    .map((stepConfig) => stepConfig.type)

  return (
    <ScreenLayout
      actions={actions}
      className={style.container}
      pageId={'Welcome'}
    >
      <PageTitle
        title={welcomeTitle}
        subTitle={welcomeSubTitle}
        shouldAutoFocus={isFirstScreen && autoFocusOnInitialScreenTitle}
      />
      {useWorkflow ? (
        <div className={style.balanceContainer}>
          <div className={style.balance} />
        </div>
      ) : forDocVideo ? (
        <DocVideoContent captureSteps={captureSteps} />
      ) : (
        <DefaultContent
          captureSteps={captureSteps}
          descriptions={customDescriptions}
        />
      )}
      {isTrial && useWorkflow && <TrialMessage />}
    </ScreenLayout>
  )
}

export default trackComponent(Welcome)
